/*
 * The administration area (spec 12 §4.5): the frame with its navigation, the account table and the
 * table of every group. What a row *says* about an account — the access, the state, the columns it
 * shares with the directory — is the people catalogue's; what only an administrator does with it is
 * here.
 */

export const en = {
	"admin.title": "Administration",
	"admin.nav.people": "Accounts",
	"admin.nav.groups": "Groups",
	"admin.nav.operations": "Operations",
	"admin.forbidden": "This page is for administrators only.",
	// A section that is planned but not built says so: "does not exist" reads as a broken link,
	// "is not built yet" reads as a stage that has not arrived. It is no longer a row in the
	// navigation — only what /admin/settings and /admin/realtime answer with.
	"admin.pending": "This section is not built yet. It arrives in a later stage.",

	// The account table.
	"admin.people.description":
		"Every account on this instance, including the deactivated ones. Access says who is an administrator, a team member or a guest.",
	"admin.people.create": "New account",
	"admin.people.create.submit": "Create and invite",
	"admin.people.empty": "No account matches that.",
	"admin.column.email": "E-mail address",
	"admin.column.lastLogin": "Last signed in",
	"admin.column.actions": "Actions",
	"admin.row.menu": "Actions for {name}",
	// A cell the account has nothing in: an empty cell for the eye, and the word for a screen reader,
	// because a cell that is blank to everybody reads as data that did not load. The dash that used
	// to stand here was a second convention for "nothing" beside the *Never* of the column next to
	// it, and one table may have one (X-B2 B2-31).
	"admin.cell.emptyName": "None",
	// Not the dash: "never signed in" is a fact about the account rather than an empty cell.
	"admin.lastLogin.never": "Never",

	// The fields of a new account. Each required one says, in the product's own words and on its own
	// line, what it is waiting for: the browser's validation bubble was the one control on this
	// screen that nobody here had written (X-B1 D10).
	"admin.field.email": "E-mail address",
	"admin.field.email.missing": "An e-mail address is needed: the invitation is sent to it.",
	"admin.field.handle": "Handle",
	"admin.field.handle.hint": "Lower-case letters, digits, dots, dashes and underscores.",
	"admin.field.handle.missing": "A handle is needed: it is how the team writes to this account.",
	"admin.field.name": "Name",
	"admin.field.name.missing": "A name is needed: it stands on every message this account sends.",

	// What an administrator does with a row.
	"admin.action.invite": "Send a new invitation",
	"admin.action.makeMember": "Make team member",
	"admin.action.makeAdmin": "Make administrator",
	"admin.action.makeGuest": "Make guest",
	"admin.action.removeAdmin": "Remove administrator rights",
	"admin.action.deactivate": "Deactivate",
	"admin.action.reactivate": "Reactivate",
	// First in the menu of an invited account whose link this screen still holds: the only link that
	// can be handed over again without retiring the live one.
	"admin.action.copyLink": "Copy invitation link",
	"admin.invite.title": "Send a new invitation to {name}?",
	"admin.invite.body":
		"The invitation that exists now stops working, and every session and trusted device of this account is signed out.",
	// A change of access is a change of the special groups (core/admin/access.ts). Every one of them
	// signs the person out everywhere, and the confirmation says so before it happens.
	"admin.confirm.makeAdmin.title": "Make {name} an administrator?",
	"admin.confirm.makeAdmin.body":
		"An administrator manages accounts, groups and every channel, but no direct message. {name} is signed out on every device and signs in again with the new access.",
	"admin.confirm.removeAdmin.title": "Remove {name}’s administrator rights?",
	"admin.confirm.removeAdmin.body":
		"{name} stays a team member, and AI chats owned by Administrators leave their lists. They are signed out on every device and sign in again with the new access. The last administrator cannot be removed.",
	"admin.confirm.removeAdmin.bodySelf":
		"You stay a team member, and AI chats owned by Administrators leave your lists. You are signed out on every device, this one included. The last administrator cannot be removed.",
	"admin.confirm.makeMember.title": "Make {name} a team member?",
	"admin.confirm.makeMember.body":
		"A team member sees every channel of the team and uses AI chats and code search. {name} is signed out on every device and signs in again with the new access.",
	"admin.confirm.makeGuest.title": "Make {name} a guest?",
	"admin.confirm.makeGuest.body":
		"{name} keeps the conversations and groups they are in, and loses the team’s other channels, AI chats and code search; their Claude logins are disconnected. They are signed out on every device and sign in again with the new access.",
	"admin.access.now.admin": "{name} is an administrator now.",
	"admin.access.now.member": "{name} is a team member now.",
	"admin.access.now.guest": "{name} is a guest now.",
	// The two kinds of account the New account form makes. Administrator is not one of them: it is
	// the row's *Make administrator*, so the mailed code it needs is asked in one flow.
	"admin.access.member.explain":
		"Sees every channel of the team, uses AI chats and code search, and makes channels and groups.",
	"admin.access.guest.explain":
		"Sees only the conversations they are added to and the channels of their groups.",
	"admin.deactivate.title": "Deactivate {name}?",
	"admin.deactivate.body":
		"They are signed out everywhere and cannot sign in again. Their messages stay where they are.",
	"admin.reactivate.title": "Reactivate {name}?",
	"admin.reactivate.body": "They can sign in again with the password they already have.",

	// The invitation link, handed over on the page the way the shell command prints it — a block under
	// the toolbar rather than a dialog over the form that made it (D-10 D11).
	"admin.link.title": "Invitation for {name}",
	"admin.link.body":
		"The invitation was e-mailed. It works once and expires on {expires}. You can also hand this link over yourself.",
	"admin.link.copy": "Copy link",
	"admin.link.copied": "Copied.",
	"admin.created": "{name} was created and invited.",
	"admin.created.guest": "{name} was created and invited as a guest.",
	"admin.invited": "A new invitation was sent to {name}.",

	// The groups section. It is the editor itself — the same screens the people directory mounts —
	// so the sentence no longer sends an administrator to another page to use it (X-A1 P11).
	"admin.groups.description":
		"Every group of the instance. An administrator creates, renames and fills a group without being in it, and decides who is in Administrators and Team members.",

	// The section that lists what Claude can read in AI chats beyond each chat's own (A-4 §7): one
	// read-only page whose parts are the modules' own; every switch is changed where it lives.
	"admin.nav.agents": "Open to AI chats",
	"admin.agents.description":
		"What Claude can read in AI chats besides each chat’s own: the drive by what Claude may do in it, and the channels and AI chats their people opened. Each is changed where it lives.",

	// The AI worker section: the machines that carry the AI chats, and the work sessions the chats
	// keep on them. It is the only window into why the AI chats are quiet, and it reads — every
	// change is a command on the server, named at the foot of the page.
	"admin.nav.work": "AI worker",
	"admin.work.description":
		"The machines that carry the AI chats, and the work sessions the chats keep on them. This page only reads: every change to it is a command on the server, named below.",
	"admin.work.loading": "Reading the machines…",
	"admin.work.disabled": "AI chats are switched off on this instance: no machine is handed a chat.",

	// One card per machine, in the facts an operator diagnoses by.
	"admin.work.workers": "Machines",
	"admin.work.workers.none": "No machine is registered yet.",
	"admin.work.worker.here": "Here",
	"admin.work.worker.away": "Away",
	"admin.work.worker.drained": "Drained — its absence is not reported",
	"admin.work.worker.never": "Has never called in",
	"admin.work.worker.lastSeen": "Last heard from",
	"admin.work.worker.build": "Build",
	// What it may run is an operator's grant: the kinds of work session it may carry.
	"admin.work.worker.allowed": "May run",
	"admin.work.worker.token": "Its token",
	"admin.work.worker.token.expires": "Expires {when}",
	"admin.work.worker.token.none": "No token that works — make one below",
	// How full the machine is, by what its stream last said: each kind of session it offers, with
	// how many of its places are taken.
	"admin.work.worker.room": "Places",
	"admin.work.worker.room.value": "{kind}: {open} of {max} taken",

	// The work sessions: what an AI chat keeps on a machine while it is open — its copy of the
	// repositories and the conversation with Claude — by where each one is.
	"admin.work.sessions": "Work sessions",
	"admin.work.sessions.lead":
		"An open AI chat keeps one work session on a machine: its copy of the repositories, and the conversation with Claude that travels over the machine’s stream.",
	"admin.work.sessions.waiting":
		"{count} work sessions wait for a machine that offers them — none is connected, or none was granted their kind.",
	"admin.work.sessions.waiting.one":
		"One work session waits for a machine that offers it — none is connected, or none was granted its kind.",
	"admin.work.sessions.lost":
		"{count} work sessions were lost in the last day instead of closing. teambase doctor on the server says which of them lost what their machine had not handed over.",
	"admin.work.sessions.lost.one":
		"One work session was lost in the last day instead of closing. teambase doctor on the server says whether it lost what its machine had not handed over.",
	"admin.work.sessions.dropped":
		"The streams turned away {count} frames of a kind this server does not know since it started: a machine on another release.",
	"admin.work.sessions.dropped.one":
		"The streams turned away one frame of a kind this server does not know since it started: a machine on another release.",
	"admin.work.sessions.column.waiting": "Waiting",
	"admin.work.sessions.column.live": "On a machine",
	"admin.work.sessions.column.attached": "Connected",
	"admin.work.sessions.column.dormant": "Asleep",
	"admin.work.sessions.column.lost": "Lost today",

	// The fact a machine's card leads with, and the work sessions table's first column.
	"admin.work.state": "State",
	"admin.work.column.kind": "Work",

	// The commands this page deliberately does not offer as buttons (S-06-D25).
	"admin.work.commands": "Changing any of this",
	"admin.work.commands.lead": "Each of these is a command on the server, run with sudo:",
	"admin.work.commands.token": "Make a machine, or replace its token",
	"admin.work.commands.token.cmd": "sudo teambase workers token --worker <name> --by <your handle>",
	"admin.work.commands.drain": "Stop reporting a machine’s absence",
	"admin.work.commands.drain.cmd": "sudo teambase workers drain --worker <name> --on",
	"admin.work.commands.undrain": "Report its absence again",
	"admin.work.commands.undrain.cmd": "sudo teambase workers drain --worker <name> --off",
	"admin.work.commands.release":
		"Free a work session a machine holds that teamBase has no record of",
	"admin.work.commands.release.cmd":
		"sudo teambase workers release <session> --worker <name> --by <your handle>",
} as const satisfies Record<string, string>
